<template>
  <nav
    class="breadcrumb"
    :class="navClasses"
    :aria-label="ariaLabelValue">
    <ol class="breadcrumb__list">
      <li
        v-for="(item, index) in displayItems"
        :key="prepeersBreadcrumbKey(item, index)"
        class="breadcrumb__list-item">
        <NuxtLink
          v-if="item.to"
          :to="localePath(item.to)"
          class="breadcrumb__link">
          {{ item.label }}
        </NuxtLink>
        <span
          v-else
          class="breadcrumb__current"
          aria-current="page">
          {{ item.label }}
        </span>
        <span
          v-if="index < displayItems.length - 1"
          class="breadcrumb__sep"
          aria-hidden="true">
          <svg
            class="breadcrumb__sep-icon"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none">
            <path
              d="M9 6L15 12L9 18"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round" />
          </svg>
        </span>
      </li>
    </ol>
  </nav>
</template>

<script lang="ts" setup>
import type { ComputedRef, PropType } from 'vue'

import type {
  PrepeersBreadcrumbItem,
  PrepeersBreadcrumbProps,
  PrepeersBreadcrumbSection,
  PrepeersBreadcrumbSectionConfig,
} from '~/types/PrepeersBreadcrumb'
import { removeHtmlToString } from '~/utils/helpers'

/** Sections supportées : clé i18n du hub + racine URL. */
const PREPEERS_BREADCRUMB_SECTIONS: Record<
  PrepeersBreadcrumbSection,
  PrepeersBreadcrumbSectionConfig
> = {
  ecoles: {
    labelKey: 'breadcrumb.ecoles',
    hubTo: '/ecoles',
    sectionRoot: 'ecoles',
  },
  metiers: {
    labelKey: 'breadcrumb.metiers',
    hubTo: '/metiers',
    sectionRoot: 'metiers',
  },
  diplomes: {
    labelKey: 'breadcrumb.diplomes',
    hubTo: '/diplomes',
    sectionRoot: 'diplomes',
  },
  'ou-etudier': {
    labelKey: 'breadcrumb.ouEtudier',
    hubTo: '/ou-etudier',
    sectionRoot: 'ou-etudier',
  },
  'types-ecoles': {
    labelKey: 'breadcrumb.typesEcoles',
    hubTo: '/types-ecoles',
    sectionRoot: 'types-ecoles',
  },
  'nos-conseils': {
    labelKey: 'breadcrumb.nosConseils',
    hubTo: '/nos-conseils',
    sectionRoot: 'nos-conseils',
  },
  recherche: {
    labelKey: 'breadcrumb.recherche',
    hubTo: '/recherche',
    sectionRoot: 'recherche',
    hubNavigable: false,
  },
}

/**
 * Définit les props du composant PrepeersBreadcrumb.
 */
const props: PrepeersBreadcrumbProps = defineProps({
  auto: {
    type: Boolean,
    default: false,
  },
  section: {
    type: String as PropType<PrepeersBreadcrumbSection | undefined>,
    default: undefined,
  },
  items: {
    type: Array as PropType<PrepeersBreadcrumbItem[] | undefined>,
    default: undefined,
  },
  ariaLabel: {
    type: String as PropType<string | undefined>,
    default: undefined,
  },
  container: {
    type: Boolean,
    default: true,
  },
  inline: {
    type: Boolean,
    default: false,
  },
  currentLabel: {
    type: String,
    default: undefined,
  },
})

const localePath = useLocalePath()
const route = useRoute()
const { t, locale, defaultLocale } = useI18n()

const navClasses: ComputedRef<(string | false)[]> = computed((): (string | false)[] => [
  props.container ? 'container' : false,
  props.inline ? 'breadcrumb--inline' : false,
])

const ariaLabelValue: ComputedRef<string> = computed(
  (): string => props.ariaLabel ?? t('breadcrumb.ariaLabel'),
)

const plainCurrentLabel: ComputedRef<string | undefined> = computed((): string | undefined => {
  const raw: string | undefined = props.currentLabel?.trim()
  if (!raw) return undefined
  return removeHtmlToString(raw)
})

const displayItems: ComputedRef<PrepeersBreadcrumbItem[]> = computed(
  (): PrepeersBreadcrumbItem[] => {
    if (props.auto && props.section) {
      return buildAutoItems(props.section)
    }
    return props.items ?? []
  },
)

/**
 * Construit le fil d'Ariane à partir de la section et des segments d'URL.
 * @param {PrepeersBreadcrumbSection} section - Section du site (hub + racine URL).
 * @returns {PrepeersBreadcrumbItem[]} Les segments à afficher (dernier sans `to` = page courante).
 */
function buildAutoItems(section: PrepeersBreadcrumbSection): PrepeersBreadcrumbItem[] {
  const config: PrepeersBreadcrumbSectionConfig = PREPEERS_BREADCRUMB_SECTIONS[section]
  const hubLabel: string = t(config.labelKey)
  const hubTo: string = config.hubTo
  const sectionRoot: string = config.sectionRoot
  const pathPrefix: string = locale.value === defaultLocale ? '' : `/${locale.value}`

  const slugParam: string | string[] | undefined = route.params.slug
  const segments: string[] = Array.isArray(slugParam) ? slugParam : slugParam ? [slugParam] : []

  const items: PrepeersBreadcrumbItem[] = [{ label: t('breadcrumb.prepeers'), to: '/' }]

  if (segments.length === 0) {
    items.push({ label: plainCurrentLabel.value || hubLabel })
    return items
  }

  items.push({ label: hubLabel, to: config.hubNavigable === false ? undefined : hubTo })

  segments.forEach((segment: string, index: number): void => {
    const isLast: boolean = index === segments.length - 1
    const slugTrail: string = segments.slice(0, index + 1).join('/')
    const path: string = pathPrefix
      ? `${pathPrefix}/${sectionRoot}/${slugTrail}`
      : `/${sectionRoot}/${slugTrail}`
    const stripTrailingNumericId: boolean =
      isLast && index > 0 && segments[index - 1] === 'programmes'
    items.push({
      label: formatSlugLabel(segment, { stripTrailingNumericId }),
      to: isLast ? undefined : path,
    })
  })

  if (plainCurrentLabel.value) {
    const lastItem: PrepeersBreadcrumbItem | undefined = items[items.length - 1]
    if (lastItem && !lastItem.to) {
      lastItem.label = plainCurrentLabel.value
    }
  }

  return items
}

/**
 * Retire l'ID numérique en suffixe de slug programme (ex. `…-6869` → `…`).
 * @param {string} slug - Segment d'URL décodé.
 * @returns {string} Slug sans suffixe `-{id}`.
 */
function stripTrailingNumericSlugId(slug: string): string {
  return slug.replace(/-\d+$/, '')
}

/**
 * Formate un segment d'URL en libellé lisible (décodage + capitalisation).
 * @param {string} slug - Segment brut issu de `route.params.slug`.
 * @param {object} [options] - Options d'affichage.
 * @param {boolean} [options.stripTrailingNumericId] - Retire un suffixe `-{id}` (slug programme).
 * @returns {string} Libellé affiché dans le fil d'Ariane.
 */
function formatSlugLabel(slug: string, options?: { stripTrailingNumericId?: boolean }): string {
  let decoded: string = slug
  try {
    decoded = decodeURIComponent(slug)
  } catch {
    decoded = slug
  }
  if (options?.stripTrailingNumericId) {
    decoded = stripTrailingNumericSlugId(decoded)
  }
  return decoded
    .replace(/[-_]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word: string): string => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Clé stable pour le `v-for` du fil d'Ariane.
 * @param {PrepeersBreadcrumbItem} item - Segment du fil d'Ariane.
 * @param {number} index - Index dans la liste.
 * @returns {string} Identifiant unique pour Vue.
 */
function prepeersBreadcrumbKey(item: PrepeersBreadcrumbItem, index: number): string {
  return item.to ? `${index}-${item.to}` : `${index}-${item.label}`
}
</script>
